import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { Button, Input } from "@learn-me/ui";
import { getErrorMessage } from "@/lib/api";
import { offeringsApi } from "@/features/offerings/api/offeringsApi";
import { Send, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";

interface FormValues {
  contactName: string;
  companyName: string;
  email: string;
  phone: string;
  message: string;
}

interface Props {
  /** Programa o capacitación desde la que se solicita, si aplica */
  offeringId?: string;
  /** Contexto que se antepone al mensaje, por ejemplo el nombre del programa */
  contextLabel?: string;
  submitLabel?: string;
}

/**
 * Formulario corto de solicitud. Se usa en la página de soluciones, en el
 * detalle de cada programa y en la página de contacto. Cuando viene de un
 * programa, la solicitud queda ligada a él para que el equipo sepa el origen.
 */
export function OfferingRequestForm({
  offeringId,
  contextLabel,
  submitLabel = "Enviar consulta",
}: Props) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>();

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      offeringsApi.submitRequest({
        companyName: values.companyName,
        contactName: values.contactName,
        email: values.email,
        phone: values.phone || undefined,
        message: contextLabel
          ? `[${contextLabel}] ${values.message}`
          : values.message,
        offeringId,
      }),
    onSuccess: () => {
      toast.success("Consulta enviada. Nos pondremos en contacto con usted.");
      reset();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  if (mutation.isSuccess) {
    return (
      <div className="text-center py-10">
        <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-secondary-900 mb-2">
          Recibimos su consulta
        </h3>
        <p className="text-secondary-600 mb-6">
          Nuestro equipo se pondrá en contacto con usted a la brevedad.
        </p>
        <Button variant="outline" onClick={() => mutation.reset()}>
          Enviar otra consulta
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit((values) => mutation.mutate(values))}
      className="space-y-4"
      noValidate
    >
      <Input
        label="Nombre"
        placeholder="Su nombre completo"
        error={errors.contactName?.message}
        {...register("contactName", {
          required: "Indique su nombre",
          maxLength: { value: 100, message: "Máximo 100 caracteres" },
        })}
      />

      <Input
        label="Empresa o institución"
        placeholder="Nombre de su organización"
        error={errors.companyName?.message}
        {...register("companyName", {
          required: "Indique su empresa o institución",
          maxLength: { value: 200, message: "Máximo 200 caracteres" },
        })}
      />

      <Input
        type="email"
        label="Correo electrónico"
        placeholder="correo@organizacion.com"
        error={errors.email?.message}
        {...register("email", {
          required: "Indique un correo electrónico",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "El correo no es válido",
          },
        })}
      />

      <Input
        type="tel"
        label="Teléfono"
        placeholder="+506 8888-8888"
        error={errors.phone?.message}
        {...register("phone", {
          maxLength: { value: 20, message: "Máximo 20 caracteres" },
        })}
      />

      <div>
        <label className="block text-sm font-medium mb-1">
          ¿En qué podemos ayudarle?
        </label>
        <textarea
          rows={4}
          placeholder="Cuéntenos brevemente qué necesidad desea resolver"
          className="w-full px-3 py-2 border border-secondary-300 rounded-lg hover:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
          {...register("message", {
            required: "Cuéntenos qué necesita",
            minLength: {
              value: 10,
              message: "Escriba al menos 10 caracteres",
            },
            maxLength: { value: 2000, message: "Máximo 2000 caracteres" },
          })}
        />
        {errors.message && (
          <p className="text-sm text-red-600 mt-1">{errors.message.message}</p>
        )}
      </div>

      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={mutation.isPending}
      >
        {mutation.isPending ? (
          "Enviando..."
        ) : (
          <>
            {submitLabel}
            <Send className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>
    </form>
  );
}
